import * as React from 'react';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, Divider, Box, Typography } from '@mui/material';
import { File as FileIcon } from '@phosphor-icons/react';

import type { CommunityFile } from '@/types/community';
import type { File } from '@/types/file';
import { getS3PresignedUrl } from '@/utils/s3';

interface DocumentPreviewCardProps {
  file: CommunityFile | File | null;
}

export function DocumentPreviewCard({ file }: DocumentPreviewCardProps): React.JSX.Element | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      if (!file) {
        setUrl(null);
        return;
      }
      try {
        const presigned = await getS3PresignedUrl(file as unknown as File);
        setUrl(presigned);
      } catch {
        setUrl(null);
      }
    };
    run();
  }, [file]);

  if (!file) return null;

  const mime = (file as any)?.mime_type as string | undefined;
  const name = (file as any)?.file?.name || (file as any)?.name || 'Document';
  const isPdf = Boolean(mime) && mime.includes('pdf');
  const isImage = Boolean(mime) && mime.includes('image');

  return (
    <Card sx={{ mt: 3 }} variant="outlined">
      <CardHeader
        avatar={<FileIcon size={20} />}
        subheader={mime || ''}
        title={name}
      />
      <Divider />
      <CardContent>
        {!url ? (
          <Typography color="text.secondary" variant="body2">
            Preview not available.
          </Typography>
        ) : isPdf ? (
          <Box sx={{ borderRadius: 1, overflow: 'hidden', height: { xs: 400, md: 600 } }}>
            <iframe src={url} style={{ width: '100%', height: '100%', border: 'none' }} title={name} />
          </Box>
        ) : isImage ? (
          <Box sx={{ textAlign: 'center' }}>
            <img alt={name} src={url} style={{ maxWidth: '100%', height: 'auto' }} />
          </Box>
        ) : (
          <Typography color="text.secondary" variant="body2">
            Preview not supported. Please download the file.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}


